import Tracking from '@vromo/analytics';
import { useStore } from '@vromo/mobx-react';
import { EntityList, Group, Role, Vehicle } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { useModals } from '@vromo/ui/components/Modals';
import Table from '@vromo/ui/components/Table';
import { useToasts } from '@vromo/ui/components/Toasts';
import Tooltip from '@vromo/ui/components/Tooltip';
import sortBy from 'lodash/sortBy';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { update } from 'serializr';
import { RootStore } from '../stores/RootStore';
import { qaLabelTeams } from './../../../../common-qa-labels/teams';
import EditableDriverRow, { BaseDriverRow } from './DriverRow';
import styles from './team.style';
import EditableUserRow, { BaseUserRow } from './UserRow';

type Props = {
    group: Group;
    vehicles: Array<Vehicle>;
    roles: EntityList<Role>;
    searchTerm?: string;
};

export default observer<Props>(function Team({ group, roles, searchTerm = '', vehicles }) {
    const {
        authStore: { graph, role },
        jobStore,
        siteStore,
        vehicleStore,
        groupStore
    } = useStore<RootStore>('rootStore');

    const { authStore } = useStore<RootStore>('rootStore');
    const isEnterpriseUser = authStore.isEnterprise;

    const UserRow = isEnterpriseUser ? BaseUserRow : EditableUserRow;
    const DriverRow = isEnterpriseUser ? BaseDriverRow : EditableDriverRow;

    const { t } = useTranslation();

    const modals = useModals();
    const showToast = useToasts();

    const isRoot = group.parents?.length == 0;
    const groupChildren = groupStore.getChildren(group);
    const teamRoles = roles.filter((role) => role.account?.id === group.id);
    const childRoles = roles.filter((r) => groupChildren.some((g) => g.id == r.account?.id));
    const childVehicles = vehicleStore.vehicles.filter((v) =>
        groupChildren.some((g) => g.id == v.group)
    );
    const filteredVehicles = useMemo(
        () => vehicles.filter((v) => v.driver?.displayName?.toLowerCase().includes(searchTerm)),
        [searchTerm, vehicles]
    );

    const filteredTeamRoles = useMemo(
        () => teamRoles.filter(({ user }) => user?.displayName?.toLowerCase().includes(searchTerm)),
        [teamRoles, searchTerm]
    );

    const canDelete =
        !isRoot &&
        !vehicles.length &&
        !teamRoles.length &&
        !vehicles.length &&
        !childRoles.length &&
        !childVehicles.length;

    const hasLinkedSites = (group: Group): string[] => {
        return siteStore.sites.items
            .filter((site) => site.group === group.id)
            .map((site) => `• ${site.contact?.name || site.id}`);
    };

    const deleteTeam = async (group: Group) => {
        if (hasLinkedSites(group).length) {
            await modals.confirm(
                t('teams.preventDelete'),
                `${t('teams.preventDeleteMessage')}\n\n${hasLinkedSites(group).join('\n')}`,
                true
            );
            return;
        }

        if (
            await modals.confirm(
                t('teams.deleteGroup'),
                t('teams.confirmDeleteGroup', { groupName: group.name })
            )
        ) {
            try {
                await graph.root.graph().roles(role.id).groups().delete({ _all_id__in: group.id });

                const jobsToRemove = jobStore.jobs.filter((job) => job.group === group.id);

                runInAction(() => {
                    update(groupStore.groups, { items: [{ id: group.id }], type: 'delete' });
                    update(jobStore.jobs, { items: jobsToRemove, type: 'delete' });
                });

                showToast(t('teams.successfullyDeleted', { groupName: group.name }));

                Tracking.track('Group Deleted', group);
            } catch (err) {
                console.error(err);
                showToast(t('teams.errorDeleting'), 'error');
            }
        }
    };

    return (
        <>
            <div className={styles.titleWrapper}>
                <div className={styles.titleContainer}>
                    <h2 data-testid={qaLabelTeams.teamDetailsNameTitle} className={styles.teamName}>
                        {group.name}
                    </h2>
                    {!isEnterpriseUser && (
                        <div>
                            <Tooltip label="Delete Team">
                                <Button
                                    destructive
                                    small
                                    data-testid={qaLabelTeams.teamDeleteIcon}
                                    disabled={!canDelete}
                                    onClick={() => deleteTeam(group)}>
                                    <FontAwesome icon="trash" />
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.teamDetailsContainer}>
                <div className={styles.dashboardUsersContainer}>
                    <FontAwesome icon="user-headset" className={styles.dashboardUsersIcon} />
                    <h3>{`Dashboard Users ${searchTerm ? `matching '${searchTerm}'` : ''}`}</h3>
                    <p className={styles.dashboardUsersCount}>
                        {`(${filteredTeamRoles.length}${
                            searchTerm ? ` of a total of ${vehicles.length}` : ''
                        })`}
                    </p>
                </div>
                {filteredTeamRoles.length > 0 ? (
                    <Table data-testid="teams--user-list">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{t('teams.dashboardUserName')}</th>
                                <th>{t('teams.dashboardUserEmail')}</th>
                                <th>{t('teams.dashboardUserRole')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortBy(filteredTeamRoles, (r) =>
                                r.user?.displayName?.trim().toLowerCase()
                            ).map((it) => (
                                <UserRow key={it.user!.id} userRole={it} roles={roles} />
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div>{t('teams.noDashboardUsers')}</div>
                )}
                <div className={styles.driversContainer}>
                    <FontAwesome icon="taxi" className={styles.driversIcon} />
                    <h3>{`Drivers ${searchTerm ? `matching '${searchTerm}'` : ''}`}</h3>
                    <p
                        data-testid={qaLabelTeams.driversNumber}
                        className={styles.driversCount}>{`(${filteredVehicles.length}${
                        searchTerm ? ` of a total of ${vehicles.length}` : ''
                    })`}</p>
                </div>
                {filteredVehicles.length > 0 ? (
                    <Table data-testid="teams--driver-list">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{t('teams.driverName')}</th>
                                <th>{t('teams.driverEmail')}</th>
                                <th>{t('teams.driverPhone')}</th>
                                <th>{t('teams.driverGroup')}</th>
                                <th>{t('teams.driverExternalIdentifier')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortBy(filteredVehicles, (v) =>
                                v
                                    .displayName(
                                        vehicleStore.driverVehicleMap,
                                        groupStore.withId(v.group!)
                                    )
                                    .trim()
                                    .toLowerCase()
                            ).map((it) => (
                                <DriverRow key={it.id} vehicle={it} />
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div>{t('teams.noDriversInGroup')}</div>
                )}
            </div>
        </>
    );
});
